import { AlarmClock, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertDto } from '@/Domain/api/Conversation/dto/Alert.dto';
import { AlertForm } from '@/Domain/components/Insights/Alerts/AlertForm/AlertForm';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { TooltipProvider } from '@/Domain/components/Shared/Tooltip/Tooltip';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useConversationAnalysisAlertCreate } from '@/Domain/hooks/Conversation/useConversationAnalysisAlertCreate.hook';

type Props = {
  alerts: AlertDto[];
};

const MAX_ALERTS = 10;

export const ManageAlertsPage = ({ alerts }: Props) => {
  const { t } = useTranslation('insights_page');
  const { handleCreateAlert, isPending } = useConversationAnalysisAlertCreate();
  const { capture } = usePostHogAnalytics();

  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);

  const isFull = alerts.length >= MAX_ALERTS;
  const remaining = MAX_ALERTS - alerts.length;
  const canSubmit = !!draftName.trim() && !!draftDescription.trim() && !isFull && !isPending;

  const handleAdd = async () => {
    if (!canSubmit) return;
    try {
      await handleCreateAlert({
        name: draftName.trim(),
        description: draftDescription.trim(),
      });
      capture(PostHogEventEnum.AlertCreated, {
        name_length: draftName.trim().length,
        description_length: draftDescription.trim().length,
      });
      setDraftName('');
      setDraftDescription('');
      setDraftError(null);
    } catch {
      setDraftError(t('manage_alerts.add_error'));
    }
  };

  return (
    <div className="py-6 space-y-6">
      <div className="rounded-xl bg-muted">
        <div className="py-6 px-6 flex items-start gap-3">
          <AlarmClock className="bg-foreground-subtle rounded-full text-white p-1 size-3.5 box-content shrink-0 mt-0.5" />
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className="font-semibold">{t('manage_alerts.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('manage_alerts.description')}</p>
          </div>
        </div>
      </div>

      <TooltipProvider>
        <div>
          <section className="grid grid-cols-[0.5fr_2fr_4fr_auto_auto] gap-x-2 py-3 px-4 text-foreground-subtle bg-muted border-b mb-4 text-sm font-medium">
            <span>{t('manage_alerts.form.header.no')}</span>
            {/* Row content sits inside an Input/<p> with px-4, so headers need
                pl-4 to line up with the start of the field text. */}
            <span className="pl-4">{t('manage_alerts.form.header.name')}</span>
            <span className="pl-4">{t('manage_alerts.form.header.description')}</span>
            <span className="pl-2 text-center">{t('manage_alerts.form.header.active')}</span>
            <span />
          </section>

          {alerts.map((alert, index) => (
            <AlertForm key={alert.id} alert={alert} index={index} />
          ))}

          {!isFull && (
            <section
              data-testid="alert-draft-row"
              className="grid grid-cols-[0.5fr_2fr_4fr_auto_auto] gap-x-2 py-2 items-start border-t pt-4 mt-2"
            >
              <p className="mt-2 text-foreground-subtle text-sm whitespace-nowrap">
                {t('manage_alerts.form.alert', { index: alerts.length + 1 })}
              </p>
              <Input
                placeholder={t('manage_alerts.form.name_placeholder')}
                size="sm"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                maxLength={100}
                data-testid="alert-draft-name"
              />
              <AutosizeTextarea
                maxLength={500}
                autoComplete="off"
                className="rounded-xl !min-h-[34px] !px-4 pb-2 pt-[9px] leading-none break-words"
                placeholder={t('manage_alerts.form.description_placeholder')}
                minHeight={12}
                value={draftDescription}
                onChange={(e) => setDraftDescription(e.target.value)}
                data-testid="alert-draft-description"
              />
              <span />
              <Button
                size="sm"
                variant="dark"
                disabled={!canSubmit}
                onClick={handleAdd}
                data-testid="alert-add-button"
              >
                <Plus className="size-4" />
                {t('button.add_alert')}
              </Button>
            </section>
          )}

          {draftError && <p className="text-sm text-red-500 mt-2">{draftError}</p>}

          <Separator className="mt-4" />
          <div className="flex justify-end items-center mt-4">
            <p className="text-sm text-foreground-subtle flex items-center">
              {t('manage_alerts.remaining_alerts')}
              <span className="font-medium ml-1">{remaining}</span>
            </p>
          </div>
        </div>
      </TooltipProvider>
    </div>
  );
};
